import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Camera, Trash2, Lock, Globe, Users, Shield,
  Loader2, Save, AlertTriangle, MapPin, BookOpen, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Header from '@/components/Header';
import api from '@/utils/api';
import { toast } from 'sonner';
import { isHeicFile, convertHeicToJpeg, ACCEPTED_IMAGE_FORMATS } from '@/utils/imageUtils';

const GROUP_CATEGORIES = [
  { id: "buy_sell", name: "Buy & Sell", icon: "💰" },
  { id: "collectors", name: "Collectors", icon: "🏆" },
  { id: "local", name: "Local Community", icon: "📍" },
  { id: "fashion", name: "Fashion & Style", icon: "👗" },
  { id: "electronics", name: "Electronics", icon: "📱" },
  { id: "vehicles", name: "Vehicles", icon: "🚗" },
  { id: "home_garden", name: "Home & Garden", icon: "🏠" },
  { id: "sports", name: "Sports & Outdoors", icon: "⚽" },
  { id: "hobbies", name: "Hobbies & Crafts", icon: "🎨" },
  { id: "other", name: "Other", icon: "📦" },
];

const GroupSettingsPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [group, setGroup] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover_image: '',
    privacy: 'public',
    category: 'other',
    location: '',
    rules: '',
    posting_permission: 'members', // 'members' or 'admins'
    join_approval: false,
    notifications_enabled: true
  });
  
  // Cover image upload
  const [uploadingCover, setUploadingCover] = useState(false);
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch {
        navigate('/auth');
      }
    };
    fetchUser();
  }, [navigate]);

  const fetchGroup = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/groups/${groupId}`);
      const groupData = res.data.group;
      setGroup(groupData);
      setIsAdmin(res.data.my_role === 'admin');
      
      if (res.data.my_role !== 'admin') {
        toast.error('Only admins can access settings');
        navigate(`/community/groups/${groupId}`);
        return;
      }
      
      setFormData({
        name: groupData.name || '',
        description: groupData.description || '',
        cover_image: groupData.cover_image || '',
        privacy: groupData.privacy || 'public',
        category: groupData.category || 'other',
        location: groupData.location || '',
        rules: groupData.rules || '',
        posting_permission: groupData.posting_permission || 'members',
        join_approval: groupData.join_approval || groupData.privacy === 'private',
        notifications_enabled: groupData.notifications_enabled !== false
      });
    } catch (error) {
      toast.error('Group not found');
      navigate('/community/groups');
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);

  useEffect(() => {
    if (user) {
      fetchGroup();
    }
  }, [user, fetchGroup]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingCover(true);
    try {
      let processedFile = file;
      if (isHeicFile(file)) {
        processedFile = await convertHeicToJpeg(file);
      }
      
      const formData = new FormData();
      formData.append('file', processedFile);
      const res = await api.post('/upload', formData);
      setFormData(prev => ({ ...prev, cover_image: res.data.url }));
      toast.success('Cover photo uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/groups/${groupId}`, formData);
      toast.success('Settings saved successfully');
      navigate(`/community/groups/${groupId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== group.name) {
      toast.error('Please type the group name to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await api.delete(`/groups/${groupId}`);
      toast.success('Group deleted successfully');
      navigate('/community/groups');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header user={user} setUser={setUser} />
        <div className="h-[184px]"></div>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  if (!group || !isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} setUser={setUser} />
      <div className="h-[184px]"></div>
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(`/community/groups/${groupId}`)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Group
        </button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Group Settings</h1>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="privacy">Privacy</TabsTrigger>
            <TabsTrigger value="rules">Rules</TabsTrigger>
            <TabsTrigger value="danger">Danger Zone</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            {/* Cover Photo */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cover Photo</CardTitle>
                <CardDescription>This image will be displayed at the top of your group page</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative h-48 bg-gray-200 rounded-lg overflow-hidden">
                  {formData.cover_image ? (
                    <img 
                      src={formData.cover_image} 
                      alt="Cover" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <Camera className="h-12 w-12" />
                    </div>
                  )}
                  <label className="absolute bottom-4 right-4 cursor-pointer">
                    <Button variant="secondary" className="shadow-lg" asChild>
                      <span>
                        {uploadingCover ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Camera className="h-4 w-4 mr-2" />
                        )}
                        Change Cover
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept={ACCEPTED_IMAGE_FORMATS}
                      className="hidden"
                      onChange={handleCoverUpload}
                      disabled={uploadingCover}
                    />
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Group Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter group name"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What is your group about?"
                    className="mt-1 min-h-24"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GROUP_CATEGORIES.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span>{cat.icon}</span>
                              <span>{cat.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Location (Optional)</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="City, Country"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Privacy Settings */}
          <TabsContent value="privacy" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Privacy & Permissions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Group Privacy */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {formData.privacy === 'private' ? (
                      <Lock className="h-5 w-5 text-amber-600" />
                    ) : (
                      <Globe className="h-5 w-5 text-emerald-600" />
                    )}
                    <div>
                      <p className="font-medium">Group Visibility</p>
                      <p className="text-sm text-gray-500">
                        {formData.privacy === 'private' 
                          ? 'Only members can see posts' 
                          : 'Anyone can see posts'}
                      </p>
                    </div>
                  </div>
                  <Select 
                    value={formData.privacy} 
                    onValueChange={(v) => setFormData(prev => ({ 
                      ...prev, 
                      privacy: v,
                      join_approval: v === 'private' ? true : prev.join_approval
                    }))}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Join Approval */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium">Require Approval to Join</p>
                      <p className="text-sm text-gray-500">
                        Admins must approve new member requests
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.join_approval}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, join_approval: v }))}
                    disabled={formData.privacy === 'private'}
                  />
                </div>

                {/* Posting Permission */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="font-medium">Who Can Post</p>
                      <p className="text-sm text-gray-500">
                        Control who can create posts in this group
                      </p>
                    </div>
                  </div>
                  <Select 
                    value={formData.posting_permission} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, posting_permission: v }))}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="members">All Members</SelectItem>
                      <SelectItem value="admins">Admins Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notifications */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="font-medium">Group Notifications</p>
                      <p className="text-sm text-gray-500">
                        Send notifications for new posts and activities
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.notifications_enabled}
                    onCheckedChange={(v) => setFormData(prev => ({ ...prev, notifications_enabled: v }))}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rules */}
          <TabsContent value="rules" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Group Rules
                </CardTitle>
                <CardDescription>
                  Set guidelines for your community. Members will see these rules before joining.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.rules}
                  onChange={(e) => setFormData(prev => ({ ...prev, rules: e.target.value }))}
                  placeholder="1. Be respectful to all members&#10;2. No spam or self-promotion&#10;3. Stay on topic&#10;4. No hate speech or harassment&#10;5. Follow BidZuu community guidelines"
                  className="min-h-48"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Tip: Use numbered lists for clarity
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Danger Zone */}
          <TabsContent value="danger" className="space-y-6">
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Danger Zone
                </CardTitle>
                <CardDescription>
                  Irreversible actions that affect your group
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-red-700">Delete This Group</p>
                      <p className="text-sm text-red-600">
                        Once deleted, all posts, members, and data will be permanently removed.
                      </p>
                    </div>
                    <Button 
                      variant="destructive"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Group
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Delete Group
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. All posts, events, media, and member data will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Type "{group.name}" to confirm:</Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={group.name}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting || deleteConfirmText !== group.name}
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete Forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupSettingsPage;
